import React from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { METRIC_INFO } from "./metricInfo";

/**
 * InfoDot — the "i" that sits in the corner of every graphical panel.
 *
 * The brief asks each graph to explain which parameters it considers. That is
 * an on-demand answer, not a permanent caption: the copy would drown the panel
 * if it were always visible, and a tooltip would be unreadable at this length.
 * So it is a small button that opens a panel of parameter rows.
 *
 * Design notes, in case this gets touched later:
 *
 *  · The popover renders in a PORTAL and is position:fixed. Panels live inside
 *    scroll containers and overflow:hidden cards; anchoring inside the card
 *    clipped it. Fixed + portal is the only placement that survives both.
 *  · It closes on Esc, on outside click, and on scroll. Scroll-close is
 *    deliberate: a fixed popover detaches from its anchor the moment the page
 *    moves, and a detached explainer pointing at nothing is worse than none.
 *  · The trigger is a real <button> with an aria-label and aria-expanded, so
 *    the panel is reachable and announced without a mouse. The content gets
 *    role="dialog" so a screen reader treats it as one unit rather than
 *    reading the parameter rows as loose text.
 *  · It stops propagation. Most panels are themselves click targets that drill
 *    down — without this, asking what a chart counts would navigate away from
 *    the chart.
 */

const POPOVER_W = 360;
const GAP = 10;

export function InfoDot({ metric, label, placement = "auto", tone = "dash" }) {
  const t = useDashTheme();
  const graphic = tone === "graphical";
  const blue = t.c.primary;
  const info = METRIC_INFO[metric];
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ left: 0, top: 0 });

  const place = React.useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const h = popRef.current?.offsetHeight || 240;
    let left = b.right - POPOVER_W;
    if (left < 10) left = 10;
    if (left + POPOVER_W > window.innerWidth - 10) left = window.innerWidth - POPOVER_W - 10;
    let top = b.bottom + GAP;
    // Flip above when the panel would run off the bottom — a drill-down page
    // can put an ⓘ near the fold, where "below" is off-screen.
    if (placement === "top" || top + h > window.innerHeight - 10) {
      top = Math.max(10, b.top - h - GAP);
    }
    setPos({ left, top });
  }, [placement]);

  React.useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  React.useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown, true);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  // An unknown metric id renders nothing rather than an ⓘ that opens an empty
  // panel — a dead affordance is worse than an absent one.
  if (!info) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={label || `What "${info.h}" counts`}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((v) => !v);
        }}
        className={graphic ? "tg-info-dot" : undefined}
        style={{
          /* Quiet ghost dot: the old solid-blue pulsing badge outshouted the
             panel it explained. It earns blue only while open. */
          width: graphic ? 20 : 19,
          height: graphic ? 20 : 19,
          borderRadius: 999,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          cursor: "pointer",
          background: open ? blue : (graphic ? "transparent" : (t.c.primarySoft || t.c.surfaceMuted)),
          color: open ? "#fff" : t.c.textTertiary,
          border: `1px solid ${open ? blue : t.c.border}`,
          padding: 0,
          transition: "transform .15s, box-shadow .15s, background .15s, color .15s, border-color .15s",
          boxShadow: open ? `0 0 0 3px ${blue}26` : "none",
        }}
      >
        <Info size={graphic ? 12 : 11} strokeWidth={2.4} />
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            className="tg-info-pop"
            role="dialog"
            aria-label={info.h}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: POPOVER_W,
              zIndex: 1400,
              background: graphic ? (t.isDark ? t.c.surface : "#F7F9FC") : t.c.surface,
              border: `1px solid ${graphic ? (t.c.borderStrong || t.c.border) : t.c.border}`,
              borderRadius: graphic ? 14 : t.r.lg + 5,
              padding: 0,
              overflow: "hidden",
              boxShadow: `0 18px 50px -12px ${t.c.shadowColorStrong || t.c.shadowColor}`,
              fontFamily: t.font.family,
              animation: "tgInfoIn .22s cubic-bezier(.16,1,.3,1)",
            }}
          >
            <div style={{ padding: graphic ? "14px 16px 14px" : 15 }}>
            <div
              style={{
                fontSize: graphic ? 11 : 10.5,
                fontWeight: 800,
                letterSpacing: 1.2,
                textTransform: "uppercase",
                color: blue,
                marginBottom: 6,
              }}
            >
              {info.k}
            </div>
            <h4 style={{
              margin: graphic ? "0 0 6px" : "0 0 12px",
              fontSize: graphic ? 15.5 : 13,
              fontWeight: graphic ? 700 : 600,
              letterSpacing: graphic ? "-0.3px" : 0,
              color: t.c.text,
              lineHeight: 1.3,
            }}>
              {info.h}
            </h4>
            {/* Hairline definition rows — the boxed bricks with blue bars
                outweighed the panel this popover explains. */}
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0 }}>
              {info.p.map(([name, desc], i) => (
                <li
                  key={name}
                  style={{
                    display: graphic ? "grid" : "flex",
                    gridTemplateColumns: graphic ? "84px 1fr" : undefined,
                    gap: 9,
                    padding: graphic ? "8px 0" : "6px 0",
                    borderTop: i === 0 ? "none" : `1px solid ${t.c.border}`,
                    borderBottom: graphic || i === info.p.length - 1 ? "none" : `1px solid ${t.c.border}`,
                    fontSize: graphic ? 12.5 : 12,
                    lineHeight: 1.55,
                  }}
                >
                  <span style={{
                    display: "block",
                    fontWeight: 700,
                    flexShrink: 0,
                    minWidth: graphic ? 0 : 88,
                    color: t.c.text,
                    fontSize: 12,
                    paddingTop: graphic ? 1 : 0,
                  }}>
                    {name}
                  </span>
                  <span style={{ color: t.c.textTertiary }}>{desc}</span>
                </li>
              ))}
            </ul>
            {info.n && (
              <div
                style={{
                  marginTop: graphic ? 10 : 12,
                  padding: graphic ? "9px 0 0" : "10px 0 0",
                  borderTop: `1px solid ${t.c.border}`,
                  fontSize: graphic ? 11.5 : 11.5,
                  color: t.c.textTertiary,
                  lineHeight: 1.55,
                  fontWeight: 500,
                }}
              >
                {info.n}
              </div>
            )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

export default InfoDot;
